export type PhotoOrTagWithRoles = {
  user_roles?: { isGeneralUser?: boolean };
  approved_for_public?: boolean;
};

/**
 * Two questions get asked of the same two fields, and they are not the same
 * question. Keeping both here, next to each other, is the only way the
 * difference stays visible.
 *
 *  - *Visibility*: may this photo or qtag be shown at all? A general user's
 *    contribution has to be approved first. @see isSociabilityTeamPhoto
 *  - *Grouping*: which of the Photos tab's two collections does it belong to?
 *    Who took it, and nothing else. @see isWalkthroughPhoto
 *
 * Approval moves a photo from hidden to shown. It does not move it from one
 * collection to the other — a user's photo stays a user's photo once approved.
 */

/**
 * Visible in the accessibility sections: team contributions, and general-user
 * ones that have been approved for public.
 *
 * NOT the Walk-through grouping, despite the name — @see isWalkthroughPhoto.
 * Mirrors web's filter in VenueDetailBasic/AccessibilitySection.tsx.
 */
export function isSociabilityTeamPhoto(item: PhotoOrTagWithRoles): boolean {
  return (
    item.user_roles?.isGeneralUser !== true ||
    item.approved_for_public === true
  );
}

/** The complement of {@link isSociabilityTeamPhoto}: hidden pending approval. */
export function isGeneralUserOnlyPhoto(item: PhotoOrTagWithRoles): boolean {
  return (
    item.user_roles?.isGeneralUser === true &&
    item.approved_for_public !== true
  );
}

/**
 * Photos tab, "Walk-through" collection: team photos only.
 *
 * Approval is deliberately not consulted. It governs whether a general user's
 * photo is public, not who took it, so an approved one belongs under "By Users"
 * with the rest of theirs. Mirrors web's split in
 * VenueDetailBasic/VenuePhotosContent.tsx.
 */
export function isWalkthroughPhoto(item: PhotoOrTagWithRoles): boolean {
  return item.user_roles?.isGeneralUser !== true;
}

/** Photos tab, "By Users" collection: every general-user photo, approved or not. */
export function isByUsersPhoto(item: PhotoOrTagWithRoles): boolean {
  return item.user_roles?.isGeneralUser === true;
}

/**
 * Whether a space card is still worth showing once its hidden photos and qtags
 * are filtered out. A Space fact carries neither by design — selecting the area
 * was the whole answer — so it survives on the flag alone.
 */
export function hasVisibleSpaceContent(space: {
  spaceFact?: boolean;
  qtags?: unknown[];
  reviewPhotos?: unknown[];
}): boolean {
  return (
    space.spaceFact === true ||
    (space.reviewPhotos?.length ?? 0) > 0 ||
    (space.qtags?.length ?? 0) > 0
  );
}

/**
 * "No bathroom onsite" before it became a Space fact, when the answer was an
 * ordinary tag on the area rather than a flag on the space.
 *
 * Still current on the backend: its accessibility widget tests
 * `qtags[0].tag_id != 743 && empty($tag['space_fact'])`, and 743 is the default
 * for `general_user_submittable_tag_ids`. Drop this once no un-migrated review
 * remains.
 */
export const LEGACY_NO_BATHROOM_TAG_ID = 743;

/**
 * Whether selecting the area was the whole answer, so there are no questions to
 * ask and no photos to expect.
 *
 * Reads the flag first and falls back to the legacy tag, so a review written
 * before the migration still reads as a Space fact.
 */
export function isSpaceFactTag(space: {
  spaceFact?: boolean;
  qtags?: { tagId?: number }[];
}): boolean {
  return (
    space.spaceFact === true ||
    space.qtags?.[0]?.tagId === LEGACY_NO_BATHROOM_TAG_ID
  );
}

/**
 * The same question asked of a tagging-flow area, which carries the flag as
 * `space_fact` (0/1) and identifies itself by `optionTitleId`.
 */
export function isSpaceFactOption(option: {
  space_fact?: number;
  optionTitleId?: number;
}): boolean {
  return (
    option.space_fact === 1 ||
    option.optionTitleId === LEGACY_NO_BATHROOM_TAG_ID
  );
}
